import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import type { AccountType } from '@generated/graphql';
import type { Account } from '@interfaces/account';
import { AccountStates } from '@interfaces/account';
import { type CustomField, FETCH_CUSTOM_FIELDS } from '@interfaces/customField';
import type { Transfer } from '@interfaces/transfer';
import { SubStepTypes } from '@interfaces/workflow';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { applicableProvinces, provinces } from '@ovComponents/resources';
import { useGlobalToast } from '@providers/globalToastProvider';
import {
  UserContext,
  useFeatureFlags,
  usePermissions,
} from '@providers/userContextProvider';
import { modifyUpdatingCustomFields } from '@utils/modifyUpdatingCustomFields';
import { find, isEmpty } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getEnabledCustomFields } from '../utils';
import { UPDATE_AFFILIATIONS } from './components/affiliateMutations';
import { AFFILIATION_STATE_FIELDS } from './components/affiliationStateFields';
import { EditAccountVisual } from './editAccount.visual';

export const FETCH_ACCOUNT_TYPES = gql`
  query fetchAccountTypes {
    fetchAccountTypes {
      accountTypes {
        value
        translatedName { ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

const ACCOUNT = (permissions?: string[]) => `#graphql
  id: stringId
  type
  state
  applyForGovFunds
  jurisdiction
  sourceOfFunds
  eligibleForDisabilityTaxCredit
  repCode { code }
  groupEmployer
  groupNumber
  rightOfSurvivorship
  subAccounts { id state }
  respAllBeneficiariesAreInBc
  user {
    id: stringId
    firstName
    lastName
    primaryEmail
    taxIdExists
    physicalAddress { country city province streetName postal unitNumber houseNumber neighborhood }
    maritalStatus
    countryOfTaxResidence
    type
    accessibleBy (input: { excludeRepCodeUsers: $excludeRepCodeUsers }) {
      relation
      organizationUser {
        id
        repCodes { code name }
      }
    }
  }
  scheduledIncomeFundTransfer {
    id
    scheduledDate
    minimumAnnualAmountCents
    annualAmountCents
    state
    subAccount {
      id
      theme { id translatedName { ${TRANSLATED_STRING_FIELDS} } }
    }
    bankAccount {
      id
      name
      bankAccountNumber
    }
    frequency
    amountPayableType
    dateOfBirth
    taxOption
    specialTaxRate
    totalWithdrawalAmountOption
  }
  householdClientGroup { id: stringId name }
  affiliations { ${AFFILIATION_STATE_FIELDS} }
  baseCurrency
  availableCurrencies
  custodianAffiliates{
    allocation
    relation
    type
    contributionAmount
    grantAmount
    user {
      dateOfBirth
      lastName
      firstName
    }
  }
  ${
    permissions && permissions.includes('read:custodian_connection')
      ? `#graphql
    custodianConnection {
      id
      name
      default
      accountTypeSettings {
        type
        baseCurrency
        availableCurrencies
        isMultiCurrencyEnabled
        affiliateSettings {
          affiliateType
          requiredFields
          optionalFields
          description
        }
      }
   }
  `
      : ''
  }
`;

const FETCH_USER_ACCOUNT = (
  derivedCustomFields?: string[],
  permissions?: string[],
) => gql`
  query fetchUserAccount($userId: SafeString!, $excludeRepCodeUsers: Boolean) {
    fetchUser(input: { userId: $userId }) {
      user {
        accounts {
          ${ACCOUNT(permissions)}
          ${
            derivedCustomFields
              ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
            key
            value
            customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format }
            selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
          }`
              : ''
          }
        }
      }
    }
  }
`;

const FETCH_ACCOUNT = (
  derivedCustomFields?: string[],
  permissions?: string[],
) => gql`
  query fetchAccount($accountId: SafeString!, $excludeRepCodeUsers: Boolean) {
    fetchAccount(input: { accountId: $accountId }) {
      account {
        ${ACCOUNT(permissions)}
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format }
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
      }
    }
  }
`;

export const UPDATE_ACCOUNT = gql`
  mutation updateAccount($input: UpdateAccountInput!) {
    updateAccount(input: $input) {
      account {
        id
      }
    }
  }
`;

const FETCH_CUSTODIAN_CONNECTIONS = gql`
  query fetchCustodianConnections($input: FetchCustodianConnectionsInput!) {
    fetchCustodianConnections(input: $input) {
      custodianConnections {
        id
        name
        default
        accountTypeSettings {
          type
          baseCurrency
          availableCurrencies
          isMultiCurrencyEnabled
          affiliateSettings {
            affiliateType
            requiredFields
            optionalFields
            description
          }
        }
      }
    }
  }
`;

export const useAccountData = () =>
  useState<
    Partial<
      Account & {
        accountId: string | null;
        householdClientGroupId?: string;
        scheduledIncomeFundTransfer: Transfer;
      }
    >
  >({ accountId: null, customFields: [] });

type FetchAccountResult = {
  fetchAccount?: {
    account?: Partial<Account>;
  };
};

export const useFetchAccount = (accountId?: string) => {
  const { permissions } = usePermissions();
  const { workflowData } = useContext(WorkflowContext);
  const { flagEnabled } = useFeatureFlags();
  return useLazyQuery<FetchAccountResult>(
    FETCH_ACCOUNT(undefined, permissions),
    {
      variables: {
        accountId: accountId || workflowData?.currentAccountId,
        skipErrorHandler: true,
        excludeRepCodeUsers: !flagEnabled('repcode_access'),
      },
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
  );
};

export const EditAccount = ({
  options,
  userId,
  onNext,
  previousStep,
  stepLoading,
  grid = false,
  updateMode = false,
  accountId,
  direction,
  workflowCompletion,
  isWidget = false,
}: {
  options: any;
  userId?: string;
  onNext: () => void;
  previousStep: () => void;
  stepLoading: boolean;
  grid?: boolean;
  updateMode?: boolean;
  accountId?: string;
  direction: 'FORWARD' | 'BACKWARD';
  workflowCompletion?: any;
  isWidget?: boolean;
}) => {
  const { permissions } = usePermissions();
  const { t } = useTranslation(['client', 'accountsDetail']);
  const { showToast } = useGlobalToast();
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const {
    activeOrganization,
    custodianConnection: defaultCustodianConnection,
  } = useContext(UserContext);
  const { flagEnabled } = useFeatureFlags();
  const [derivedCustomFields, setDerivedCustomFields] =
    useState<CustomField[]>();
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] =
    useState<string[]>();
  const [custodianConnections, setCustodianConnections] = useState<any[]>();
  const [accountData, setAccountData] = useAccountData();
  const [submitting, setSubmitting] = useState(false);
  const enabledCustomFields = getEnabledCustomFields(options);
  const skipFetchAccount =
    submitting ||
    (((!accountId && !workflowData.currentAccountId) ||
      (permissions.includes('read:custom_fields') &&
        enabledCustomFields.length !== 0)) &&
      !workflowData.skipEditAccount);
  const skipFetchUserAccount =
    (!!workflowData.currentAccountId ||
      !!accountId ||
      (permissions.includes('read:custom_fields') &&
        enabledCustomFields.length !== 0)) &&
    !workflowData.skipEditAccount;
  const [isCurrencyFieldsDisabled, setIsCurrencyFieldsDisabled] =
    useState<boolean>(false);

  const { data: accountTypesData } = useQuery(FETCH_ACCOUNT_TYPES, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id ?? undefined,
        },
      },
      skip:
        !permissions.includes('read:account_types') ||
        workflowData.skipEditAccount ||
        !accountData.type,
    },
  });

  const [currentAccountTypeData, setCurrentAccountTypeData] =
    useState<AccountType | null>(null);

  useEffect(() => {
    if (accountTypesData) {
      const currentAccountType = accountData.type;
      const currentAccountTypeData =
        accountTypesData?.fetchAccountTypes?.accountTypes?.find(
          (a: AccountType) => a.value === currentAccountType,
        );
      if (currentAccountTypeData) {
        setCurrentAccountTypeData(currentAccountTypeData);
      }
    }
  }, [accountTypesData, accountData.type]);

  const { loading: customFieldLoading } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          workflowStep: SubStepTypes.EDIT_ACCOUNT,
          organizationId: activeOrganization?.id ?? undefined,
          keys: !isEmpty(enabledCustomFields) ? enabledCustomFields : undefined,
        },
        pagination: { perPage: 100 },
      },
      skipErrorHandler: true,
    },
    skip:
      (!permissions.includes('read:custom_fields') ||
        enabledCustomFields.length === 0) &&
      !workflowData.skipEditAccount,
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
    onCompleted: async (res: any) => {
      const customFieldKeys = res?.fetchCustomFields?.customFields?.map(
        (a: any) => a.key,
      );
      setDerivedCustomFields(res?.fetchCustomFields?.customFields);

      if (accountId || workflowData.currentAccountId) {
        await fetchAccount({
          query: customFieldKeys
            ? FETCH_ACCOUNT(customFieldKeys, permissions)
            : undefined,
        });
      } else {
        await fetchUserAccount({
          variables: {
            userId,
            skipErrorHandler: true,
            excludeRepCodeUsers: !flagEnabled('repcode_access'),
          },
          query: customFieldKeys
            ? FETCH_USER_ACCOUNT(customFieldKeys, permissions)
            : undefined,
        });
      }
    },
  });

  const { loading: custodianConnectionsLoading } = useQuery(
    FETCH_CUSTODIAN_CONNECTIONS,
    {
      variables: {
        input: {
          filter: { organizationId: activeOrganization?.id ?? undefined },
          pagination: { perPage: 100 },
        },
        skipErrorHandler: true,
      },
      skip:
        (!options.custodianSelector ||
          !permissions.includes('read:custodian_connection')) &&
        !workflowData.skipEditAccount,
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
      onCompleted: async (res: any) => {
        setCustodianConnections(
          res?.fetchCustodianConnections?.custodianConnections,
        );
      },
    },
  );

  const [fetchAccount, { data, refetch: accountRefetch, loading }] =
    useFetchAccount(accountId);

  const setAccountDataFunc = (response: any): void => {
    setAccountData((state: typeof accountData) => {
      const uniqueCustomFieldsFromResponse =
        response.fetchAccount.account.customFields?.filter(
          (cf: { key: string }) =>
            !state.customFields?.some(
              (_cf: { key: string }) => _cf.key === cf.key,
            ),
        ) || [];
      const accountState =
        response.fetchAccount.account?.state ?? AccountStates.INITIATED;

      if (
        ![AccountStates.INITIATED, AccountStates.READY].includes(accountState)
      ) {
        setIsCurrencyFieldsDisabled(true);
      } else {
        setIsCurrencyFieldsDisabled(false);
      }

      return {
        ...response.fetchAccount.account,
        state: accountState,
        accountId: response.fetchAccount.account.id,
        custodianAffiliates:
          response.fetchAccount.account.custodianAffiliates || [],
        sourceOfFunds: response.fetchAccount.account.sourceOfFunds ?? undefined,
        eligibleForDisabilityTaxCredit:
          response.fetchAccount.account.eligibleForDisabilityTaxCredit ??
          undefined,
        jurisdiction: response.fetchAccount.account.jurisdiction ?? undefined,
        householdClientGroupId:
          response.fetchAccount.account.householdClientGroup?.id ?? undefined,
        customFields: [
          ...uniqueCustomFieldsFromResponse,
          ...(state.customFields || []),
        ],
        baseCurrency:
          response.fetchAccount.account.baseCurrency ?? CurrencyCodes.CAD,
        availableCurrencies:
          response.fetchAccount.account.availableCurrencies &&
          response.fetchAccount.account.availableCurrencies.length > 0
            ? response.fetchAccount.account.availableCurrencies
            : [CurrencyCodes.CAD],
        custodianConnection:
          response.fetchAccount.account.custodianConnection ??
          defaultCustodianConnection,
        respAllBeneficiariesAreInBc:
          response.fetchAccount.account.respAllBeneficiariesAreInBc,
      };
    });
  };

  const {
    data: defaultdata,
    refetch,
    loading: fetchAccountLoading,
  } = useQuery(FETCH_ACCOUNT(undefined, permissions), {
    variables: {
      accountId: accountId || workflowData?.currentAccountId,
      skipErrorHandler: true,
    },
    fetchPolicy: 'network-only',
    skip: skipFetchAccount,
    errorPolicy: 'ignore',
  });

  const [
    fetchUserAccount,
    { data: fetchedUserAccountData, refetch: refetchUserAccount },
  ] = useLazyQuery(FETCH_USER_ACCOUNT(undefined, permissions), {
    variables: {
      userId,
      skipErrorHandler: true,
      excludeRepCodeUsers: !flagEnabled('repcode_access'),
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
  });

  const {
    data: userData,
    refetch: userRefetch,
    loading: userLoading,
  } = useQuery(FETCH_USER_ACCOUNT(undefined, permissions), {
    variables: {
      userId,
      skipErrorHandler: true,
      excludeRepCodeUsers: !flagEnabled('repcode_access'),
    },
    fetchPolicy: 'network-only',
    skip: skipFetchUserAccount,
    errorPolicy: 'ignore',
  });

  useEffect(() => {
    if (derivedCustomFields) {
      const fields = derivedCustomFields.map((a) => a.key);
      setDerivedCustomFieldsKeys(fields);
    }
  }, [derivedCustomFields]);

  const [updateAccount, { loading: updateAccountLoading }] = useMutation(
    UPDATE_ACCOUNT,
    {
      onCompleted: () => onNext(),
      variables: {
        input: {
          accountId: accountData.accountId,
          applyForGovFunds: accountData.applyForGovFunds,
          jurisdiction: accountData.jurisdiction ?? undefined,
          sourceOfFunds: accountData.sourceOfFunds ?? undefined,
          groupEmployer: accountData.groupEmployer ?? undefined,
          groupNumber: accountData.groupNumber ?? undefined,
          eligibleForDisabilityTaxCredit:
            accountData.eligibleForDisabilityTaxCredit ?? undefined,
          householdClientGroupId:
            accountData.householdClientGroupId ?? undefined,
          customFields: modifyUpdatingCustomFields(
            accountData?.customFields || [],
            derivedCustomFields || [],
            derivedCustomFieldsKeys,
          ),
          ...(isCurrencyFieldsDisabled
            ? {}
            : { baseCurrency: accountData.baseCurrency }),
          ...(isCurrencyFieldsDisabled
            ? {}
            : { availableCurrencies: accountData.availableCurrencies }),
          custodianConnectionId:
            accountData.custodianConnection?.id ?? undefined,
          repCode: accountData.repCode?.code,
          rightOfSurvivorship: accountData.rightOfSurvivorship ?? undefined,
          respAllBeneficiariesAreInBc:
            accountData.respAllBeneficiariesAreInBc ?? undefined,
        },
      },
    },
  );

  const [updateAffiliationsMutation] = useMutation(UPDATE_AFFILIATIONS, {
    variables: {
      input: {
        accountId: accountData.accountId,
        affiliations:
          accountData.affiliations?.map((item) => ({
            id: item.id,
            allocation: item.allocation,
            relation: item.relation,
            type: item.type,
            userId: item.user.id,
            relatedAffiliations: item.relatedAffiliations,
            invitationStatus: item?.invitationStatus,
            roleId: item?.roleId,
          })) || [],
      },
    },
  });
  const updateAffiliations = async () => {
    await updateAffiliationsMutation();
    await setWorkflowData((prev) => ({
      ...prev,
      currentAccountId: accountData.accountId || undefined,
      currentAccountType: accountData.type,
    }));
    await updateAccount();
    if (updateMode) {
      showToast({
        message: t('shared:profileDetailsUpdated'),
        severity: 'success',
      });
    }
  };

  useEffect(() => {
    if (data || defaultdata) {
      setAccountDataFunc(data ?? defaultdata);
    }
  }, [data, defaultdata]);

  const setUserAccountDataFunc = (response: any): void => {
    const firstAccount = response.fetchUser.user.accounts[0];
    if (!firstAccount) {
      return;
    }
    setAccountData({
      state: firstAccount?.state ?? AccountStates.INITIATED,
      accountId: firstAccount.id,
      type: firstAccount.type || undefined,
      repCode: firstAccount.repCode,
      groupEmployer: firstAccount.groupEmployer ?? undefined,
      groupNumber: firstAccount.groupNumber ?? undefined,
      applyForGovFunds: firstAccount.applyForGovFunds || [],
      user: firstAccount.user,
      affiliations: firstAccount.affiliations || [],
      custodianAffiliates: firstAccount.custodianAffiliates || [],
      subAccounts: firstAccount.subAccounts,
      scheduledIncomeFundTransfer: firstAccount.scheduledIncomeFundTransfer,
      sourceOfFunds: firstAccount.sourceOfFunds ?? undefined,
      jurisdiction: firstAccount.jurisdiction ?? undefined,
      householdClientGroupId: firstAccount.householdClientGroupId ?? undefined,
      customFields: firstAccount?.customFields || [],
      baseCurrency: firstAccount?.baseCurrency ?? CurrencyCodes.CAD,
      availableCurrencies:
        firstAccount?.availableCurrencies &&
        firstAccount?.availableCurrencies.length > 0
          ? firstAccount?.availableCurrencies
          : [CurrencyCodes.CAD],
      custodianConnection:
        firstAccount?.custodianConnection ?? defaultCustodianConnection,
    });
  };

  useEffect(() => {
    if (userData || fetchedUserAccountData) {
      setUserAccountDataFunc(userData ?? fetchedUserAccountData);
    }
  }, [
    userData,
    onNext,
    fetchedUserAccountData,
    defaultCustodianConnection,
    setAccountData,
  ]);

  const filterApplicableProvinces = (province: {
    key: string;
    value: string;
  }) => {
    const currentAccountType = accountData?.type;
    const availabileRestriction: any = find(applicableProvinces, {
      accountType: currentAccountType,
    });
    if (availabileRestriction) {
      return availabileRestriction.availability.includes(province.key);
    }
    return true;
  };

  function getJurisdictionOptions(): Array<{ id?: string; label?: string }> {
    return provinces
      .filter(filterApplicableProvinces)
      .map((a: any) => ({ id: a.key, label: a.value }));
  }

  function getSourceOfFundsOptions(): Array<{
    id?: string;
    label?: string;
    key?: string;
  }> {
    const currentAccountType = accountData?.type;
    if (currentAccountType?.startsWith('USA_')) {
      return [
        'USA_EMPLOYMENT_WAGES',
        'USA_INHERITANCE_TRUST',
        'USA_LOTTERY_GAMING',
        'USA_RETIREMENT_FUNDS',
        'USA_INVESTMENTS',
        'USA_SPOUSAL_PARENTAL_SUPPORT',
        'USA_GIFT',
        'USA_SAVINGS',
        'USA_UNEMPLOYMENT_DISABILITY',
        'USA_LEGAL_SETTLEMENT',
        'USA_OTHER',
      ].map((type) => ({
        id: type,
        label: t(
          `workflowCompletions:updateAccount.sourceOfFundsOptions.${type}`,
        ),
      }));
    }
    return ['SELF', 'SPOUSE'].map((type) => ({
      id: type,
      label: t(
        `workflowCompletions:updateAccount.sourceOfFundsOptions.${type}`,
      ),
    }));
  }

  const getCurrentJurisdictionOption = (key: string) => {
    if (key === '') return undefined;
    const current = provinces.filter((k: any) => k.key === key);
    if (current && current[0]) {
      return current[0].key;
    }
    return undefined;
  };

  const handleSkip = async () => {
    const accountIdToSet =
      accountData.accountId || accountId || workflowData.currentAccountId;
    const accountTypeToSet =
      accountData.type || workflowData.currentAccountType;

    if (accountIdToSet || accountTypeToSet) {
      await setWorkflowData((prev) => ({
        ...prev,
        ...(accountIdToSet && { currentAccountId: accountIdToSet }),
        ...(accountTypeToSet && { currentAccountType: accountTypeToSet }),
      }));
    }
    await onNext();
  };

  return (
    <EditAccountVisual
      userId={userId}
      options={options}
      accountData={accountData}
      updateAccount={setAccountData}
      continueFunc={async () => {
        setSubmitting(true);
        await updateAffiliations();
        setSubmitting(false);
      }}
      loadingData={
        customFieldLoading ||
        loading ||
        fetchAccountLoading ||
        userLoading ||
        custodianConnectionsLoading
      }
      loading={
        customFieldLoading ||
        loading ||
        fetchAccountLoading ||
        userLoading ||
        custodianConnectionsLoading ||
        updateAccountLoading ||
        stepLoading
      }
      refetch={async () => {
        if (workflowData.currentAccountId || accountId) {
          if (skipFetchAccount) {
            const result = await accountRefetch();
            if (result?.data) setAccountDataFunc(result.data);
          } else {
            const result = await refetch();
            if (result?.data) setAccountDataFunc(result.data);
          }
        } else if (skipFetchUserAccount) {
          const result = await refetchUserAccount();
          if (result?.data) setUserAccountDataFunc(result.data);
        } else {
          const result = await userRefetch();
          if (result?.data) setUserAccountDataFunc(result.data);
        }
      }}
      grid={grid}
      updateMode={updateMode}
      skip={direction === 'FORWARD' ? handleSkip : previousStep}
      filterApplicableProvinces={filterApplicableProvinces}
      getJurisdictionOptions={getJurisdictionOptions}
      getSourceOfFundsOptions={getSourceOfFundsOptions}
      getCurrentJurisdictionOption={getCurrentJurisdictionOption}
      activeCustomFields={derivedCustomFieldsKeys}
      derivedCustomFields={derivedCustomFields}
      custodianConnections={custodianConnections}
      workflowCompletion={workflowCompletion}
      skipEditAccount={workflowData.skipEditAccount ?? false}
      isWidget={isWidget}
      isCurrencyFieldsDisabled={isCurrencyFieldsDisabled}
      currentAccountTypeData={currentAccountTypeData}
    />
  );
};

export default EditAccount;
